"use client";

import { useState, useRef, useEffect } from "react";
import { Camera, X, Loader2, Hash } from "lucide-react";
import { successToast,errorToast } from '../utils/toasters'
import MainLayout from "../components/MainLayout";
import { handleAddGroup } from "../services/group";
import { getScoops } from "../services/scoops";
import { useNavigate, Link, useLocation } from "react-router-dom";

function CreateGroupPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [scoops, setScoops] = useState([]);
  const [selectedScoopId, setSelectedScoopId] = useState(null);
  const [loadingScoops, setLoadingScoops] = useState(true);
  const [autoSubmitPending, setAutoSubmitPending] = useState(false);

  const fileInputRef = useRef(null);

  // Key for storing form data in memory
  const FORM_DATA_KEY = "createGroupFormData";

  useEffect(() => {
    fetchScoops();

    // Check if user came back from login
    const urlParams = new URLSearchParams(location.search);
    const fromLogin = urlParams.get("fromLogin") === "true";

    if (fromLogin) {
      restoreFormData();
      // Clean up URL
      navigate("/groups/create", { replace: true });
    }
  }, [location.search]);

  // Save form data to sessionStorage before redirect
  const saveFormData = () => {
    const formData = {
      title,
      description,
      coverImage,
      selectedScoopId,
      // Note: We can't save the actual file, but we can save the image URL for preview
      timestamp: Date.now(),
    };

    try {
      sessionStorage.setItem(FORM_DATA_KEY, JSON.stringify(formData));
      // Also save the file data if it exists (convert to base64 for storage)
      if (imageFile) {
        const reader = new FileReader();
        reader.onload = function (e) {
          sessionStorage.setItem(FORM_DATA_KEY + "_file", e.target.result);
          sessionStorage.setItem(FORM_DATA_KEY + "_fileName", imageFile.name);
          sessionStorage.setItem(FORM_DATA_KEY + "_fileType", imageFile.type);
        };
        reader.readAsDataURL(imageFile);
      }
    } catch (error) {
      console.error("Error saving form data:", error);
    }
  };

  // Restore form data after login
  const restoreFormData = () => {
    try {
      const savedData = sessionStorage.getItem(FORM_DATA_KEY);
      const savedFile = sessionStorage.getItem(FORM_DATA_KEY + "_file");
      const savedFileName = sessionStorage.getItem(FORM_DATA_KEY + "_fileName");
      const savedFileType = sessionStorage.getItem(FORM_DATA_KEY + "_fileType");

      if (savedData) {
        const formData = JSON.parse(savedData);
        // Check if data is not too old (24 hours)
        if (Date.now() - formData.timestamp < 24 * 60 * 60 * 1000) {
          setTitle(formData.title || "");
          setDescription(formData.description || "");
          setCoverImage(formData.coverImage);
          setSelectedScoopId(formData.selectedScoopId);

          // Restore file if it exists
          if (savedFile && savedFileName && savedFileType) {
            fetch(savedFile)
              .then((res) => res.blob())
              .then((blob) => {
                const file = new File([blob], savedFileName, {
                  type: savedFileType,
                });
                setImageFile(file);
              })
              .catch((error) => console.error("Error restoring file:", error));
          }

          // Set auto-submit flag
          setAutoSubmitPending(true);

          successToast("Welcome back! Your form data has been restored.", {
            position: "top-right",
            autoClose: 3000,
          });
        }

        // Clean up stored data
        clearSavedFormData();
      }
    } catch (error) {
      console.error("Error restoring form data:", error);
    }
  };

  // Clear saved form data
  const clearSavedFormData = () => {
    try {
      sessionStorage.removeItem(FORM_DATA_KEY);
      sessionStorage.removeItem(FORM_DATA_KEY + "_file");
      sessionStorage.removeItem(FORM_DATA_KEY + "_fileName");
      sessionStorage.removeItem(FORM_DATA_KEY + "_fileType");
    } catch (error) {
      console.error("Error clearing saved form data:", error);
    }
  };

  // Auto-submit when data is restored and form is valid
  useEffect(() => {
    if (autoSubmitPending && !loadingScoops && scoops.length > 0) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        if (validateForm()) {
          successToast("Auto-submitting your group...", {
            position: "top-right",
            autoClose: 2000,
          });
          handleSubmit(new Event("submit"));
        } else {
          successToast("Please complete the form to create your group.", {
            position: "top-right",
            autoClose: 3000,
          });
        }
        setAutoSubmitPending(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [
    autoSubmitPending,
    loadingScoops,
    scoops,
    title,
    description,
    imageFile,
    selectedScoopId,
  ]);

  const fetchScoops = async () => {
    try {
      setLoadingScoops(true);
      const response = await getScoops();

      if (response) {
        setScoops(response);

        // Find the "Galli Talks" scoop and set it as selected (if not already set from restored data)
        if (!selectedScoopId) {
          const galliTalksScoop = response.find(
            (scoop) => scoop.scoopname === "Galli Talks"
          );

          if (galliTalksScoop) {
            setSelectedScoopId(galliTalksScoop.scoopid);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching scoops:", error);
    } finally {
      setLoadingScoops(false);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const imageUrl = URL.createObjectURL(file);
      setCoverImage(imageUrl);
      setErrors({ ...errors, coverImage: undefined });
    }
  };

  const removeImage = () => {
    setCoverImage(null);
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = "Group name is required";
    } else if (title.length < 3) {
      newErrors.title = "Group name must be at least 3 characters";
    }

    if (!description.trim()) {
      newErrors.description = "Description is required";
    } else if (description.length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }

    if (!imageFile) {
      newErrors.coverImage = "Cover image is required";
    }

    if (!selectedScoopId) {
      newErrors.topic = "Topic selection is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check authentication before proceeding
    const access_token = localStorage.getItem("access_token");
    if (!access_token) {
      saveFormData();

      successToast(
        "Please login to create a group. Your form data will be saved.",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        }
      );
      navigate(
        `/signin?returnUrl=${encodeURIComponent(
          "/groups/create?fromLogin=true"
        )}`
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const geohash = localStorage.getItem("geohash") || "9v6m";

      const formData = new FormData();
      formData.append("name", title);
      formData.append("description", description);
      formData.append("scoopId", selectedScoopId);
      formData.append("geoHash", geohash);

      if (imageFile) {
        formData.append("coverphoto", imageFile);
      }

      const response = await handleAddGroup(formData);

      // Clear any saved form data on success
      clearSavedFormData();

      successToast("Group created successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });

      navigate("/groups");
    } catch (error) {
      console.error("Error creating group:", error);

      errorToast(
        "" +
          (error.response?.data?.message ??
            error.data?.message ??
            error.message ??
            "Failed to create group"),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear saved data when user navigates away or closes tab
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only clear if form is empty or successfully submitted
      if (!title && !description && !imageFile) {
        clearSavedFormData();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [title, description, imageFile]);

  // Find the selected scoop (topic)
  const selectedScoop = scoops.find(
    (scoop) => scoop.scoopid === selectedScoopId
  );

  return (
    <MainLayout rs={false}>
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold font-fraunces">
            Create a New Group
          </h1>
          <p className="text-gray-600 font-afacad">
            Connect with people who share your interests
          </p>
          {autoSubmitPending && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm font-afacad">
                <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
                Preparing to auto-submit your group...
              </p>
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow-sm border overflow-visible"
        >
          {/* Cover Image Section */}
          <div className="relative">
            <div
              className={`h-48 w-full bg-gray-100 flex items-center justify-center cursor-pointer overflow-hidden ${
                errors.coverImage ? "border-2 border-red-500" : ""
              }`}
              onClick={handleImageClick}
            >
              {coverImage ? (
                <img
                  src={coverImage || "/placeholder.svg"}
                  alt="Group cover"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="text-center">
                  <Camera className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Click to upload a cover image</p>
                </div>
              )}
            </div>

            {coverImage && (
              <button
                type="button"
                className="absolute top-2 right-2 bg-black/70 text-white p-1 rounded-full"
                onClick={removeImage}
              >
                <X className="h-5 w-5" />
              </button>
            )}

            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleImageChange}
            />

            {errors.coverImage && (
              <p className="text-red-500 text-sm mt-1 px-4">
                {errors.coverImage}
              </p>
            )}
          </div>

          {/* Form Fields */}
          <div className="p-6 space-y-4">
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 mb-1 font-afacad"
              >
                Group Name*
              </label>
              <input
                type="text"
                id="title"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-afacad ${
                  errors.title ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter a name for your group"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) {
                    setErrors({ ...errors, title: undefined });
                  }
                }}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1 font-afacad">
                  {errors.title}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-1 font-afacad"
              >
                Description*
              </label>
              <textarea
                id="description"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none font-afacad ${
                  errors.description ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="What's your group about?"
                rows={4}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) {
                    setErrors({ ...errors, description: undefined });
                  }
                }}
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1 font-afacad">
                  {errors.description}
                </p>
              )}
            </div>

            {/* Display the fixed topic with data from API */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-afacad">
                Group Topic
              </label>
              <div
                className={`w-full px-3 py-2 border rounded-lg text-left flex items-center bg-gray-50 ${
                  errors.topic ? "border-red-500" : "border-gray-300"
                }`}
              >
                {loadingScoops ? (
                  <div className="flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <span className="font-afacad">Loading topics...</span>
                  </div>
                ) : selectedScoop ? (
                  <div className="flex items-center">
                    <div className="w-6 h-6 rounded-full bg-purple-300 text-purple-800 flex items-center justify-center mr-2">
                      <Hash size={14} />
                    </div>
                    <span className="font-afacad">
                      {selectedScoop.scoopname}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-500 font-afacad">
                    Topic not available
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1 font-afacad">
                All groups are currently assigned to Galli Talks
              </p>
              {errors.topic && (
                <p className="text-red-500 text-sm mt-1 font-afacad">
                  {errors.topic}
                </p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t flex justify-end space-x-3">
            <Link
              to="/groups"
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-afacad"
              onClick={clearSavedFormData}
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting || loadingScoops || autoSubmitPending}
              className="px-4 py-2 bg-[#7B189F] text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-afacad"
            >
              {isSubmitting || autoSubmitPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {autoSubmitPending ? "Auto-submitting..." : "Creating..."}
                </>
              ) : (
                "Create Group"
              )}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
}

export default CreateGroupPage;
